import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ButtonRounded } from '../UISystem/Buttons';
import { SHADOWS } from '../../styles/UISystem';

/**
 * Shown after a hard browser/OS location denial. Explains how to re-enable in
 * Settings (and, on an installed iOS PWA, that the app must be force-quit for the
 * change to take effect) and lets the user continue without location.
 *
 * Standard slide-up bottom sheet (ui-design-system.md §4.2). Terminal by design:
 * a single dismiss + backdrop tap. It never re-triggers the location primer —
 * that "Try Again → primer → re-fail" cycle was the inescapable loop we removed.
 */
const BrowserPermissionModal = ({ onClose }) => {
  // Detect browser/OS for the right Settings steps
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  // Body scroll lock while the sheet is open (ui-design-system.md §4.7)
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const getInstructions = () => {
    if (isIOS) {
      return (
        <ol style={{ textAlign: 'left', paddingLeft: '20px', margin: 0 }}>
          <li>Open Settings app</li>
          <li>Scroll down to Apps &gt; Safari</li>
          <li>Scroll down and tap &quot;Location&quot;</li>
          <li>Set access to &quot;Allow&quot;</li>
        </ol>
      );
    } else if (isAndroid) {
      return (
        <ol style={{ textAlign: 'left', paddingLeft: '20px', margin: 0 }}>
          <li>Tap the three dots (menu) in your browser</li>
          <li>Tap &quot;Settings&quot; → &quot;Site settings&quot;</li>
          <li>Tap &quot;Location&quot;</li>
          <li>Find this website and change the permission</li>
        </ol>
      );
    } else if (isSafari) {
      return (
        <ol style={{ textAlign: 'left', paddingLeft: '20px', margin: 0 }}>
          <li>Click Safari in the menu bar</li>
          <li>Click &quot;Settings for This Website&quot;</li>
          <li>Change Location permission to &quot;Allow&quot;</li>
        </ol>
      );
    } else {
      return (
        <ol style={{ textAlign: 'left', paddingLeft: '20px', margin: 0 }}>
          <li>Click the lock/info icon in the address bar</li>
          <li>Click &quot;Site settings&quot;</li>
          <li>Change Location permission from &quot;Block&quot; to &quot;Allow&quot;</li>
        </ol>
      );
    }
  };

  return (
    <motion.div
      className="browser-permission-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2000,
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
      }}
    >
      <motion.div
        className="browser-permission-sheet"
        initial={{ y: 500 }}
        animate={{ y: 0 }}
        exit={{ y: 500 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          backgroundColor: 'white',
          borderTopLeftRadius: '24px',
          borderTopRightRadius: '24px',
          boxShadow: SHADOWS.TIER3,
          padding: '32px 20px calc(40px + env(safe-area-inset-bottom))',
          textAlign: 'center',
        }}
      >
        <h3 style={{ margin: '0 0 12px', color: '#333' }}>Location Access Blocked</h3>

        <p style={{ margin: '0 0 16px', color: '#666', fontSize: '16px' }}>
          To share location, please update your browser settings
        </p>

        <div
          style={{
            margin: '0 0 16px',
            padding: '12px',
            backgroundColor: '#f8f8f8',
            borderRadius: '8px',
          }}
        >
          {getInstructions()}
        </div>

        {/* iOS PWA only: the Settings change won't take effect in the running app
            until it's force-quit and relaunched (WebKit caches the live grant). */}
        {isIOS && (
          <p style={{ margin: '0 0 4px', color: '#333', fontSize: '15px', fontWeight: 600 }}>
            Then force quit the Royale app and restart
          </p>
        )}

        {/* Single, terminal dismiss — no "Try Again" that would re-loop the primer */}
        <div style={{ marginTop: '20px' }}>
          <ButtonRounded
            text="Continue without location"
            variant="secondary"
            size="full"
            onClick={onClose}
          />
        </div>
      </motion.div>
    </motion.div>
  );
};

export default BrowserPermissionModal;
